import Button from "../../../../components/Button";

export default function PrintAllTracerButton({
  styles,
  activeTab,
  forms,
  pharmacyInfo,
  user,
  effectiveShowRepeat,
  repeatData,
  repeatNumberFromNav,
}) {
  if (!(activeTab === "Tracer Label" && forms.length > 0)) return null;

  const handlePrintAllTracer = async () => {
    const allForms = Array.isArray(forms) ? forms : [];

    const printStyles = document.createElement("style");
    printStyles.textContent = `
      @media print {
        /* Hide everything except our print labels */
        body > *:not(.printOnlyLabel) {
          display: none !important;
        }

        /* Show only the print labels and their content */
        .printOnlyLabel, .printOnlyLabel * {
          display: block !important;
          visibility: visible !important;
        }

        /* Position the labels at the top of the page with no extra space */
        .printOnlyLabel {
          position: relative !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          height: auto !important;
          background: white !important;
          padding: 0 !important;
          margin: 0 !important;
          box-sizing: border-box !important;
          overflow: hidden !important;
          font-family: Helvetica, Condensed !important;
          font-size: 11px !important;
          line-height: 1.1 !important;
          color: #000 !important;
          font-weight: bold !important;
          page-break-after: always !important;
          page-break-inside: avoid !important;
          page-break-before: always !important;
        }

        /* Remove all margins and padding from body and html */
        body, html {
          margin: 0 !important;
          padding: 0 !important;
          height: auto !important;
        }

        /* Minimize page margins */
        @page {
          size: auto;
          margin: 0mm !important;
        }
      }

      /* Hide the print-only labels when not printing */
      @media screen {
        .printOnlyLabel {
          position: absolute !important;
          left: -9999px !important;
          top: -9999px !important;
          visibility: hidden !important;
          z-index: -9999 !important;
        }
      }
    `;

    const createItemLabelHTML = (form) => {
      const currentForm = form;
      const medicationName = currentForm?.data?.medication || "MEDICATION NAME";
      const directions = currentForm?.data?.directions || "Take as directed";
      const rxNumber = currentForm?.data?.rxNumber || "1416650";
      const patientName = currentForm?.data?.patientName || "PATIENT NAME";
      const practitionerName = currentForm?.data?.practitioner
        ? `${currentForm.data.practitioner.surname?.toUpperCase() || "PRACTITIONER"} ${
            currentForm.data.practitioner.firstname?.charAt(0)?.toUpperCase() || ""
          }.`
        : "DOCTOR";
      const currentDate = new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
      const dispenserName = user
        ? `${user.surname?.toUpperCase() || "DISPENSER"} ${
            user.name?.toUpperCase() || "NAME"
          }`
        : "DISPENSER NAME";

      const drugInfo = currentForm?.data?.drugInfo;

      let medicationDetails = medicationName;

      let extractedStrength = "";
      if (!drugInfo?.strength && medicationName) {
        const strengthMatch = medicationName.match(
          /\b\d+(?:\.\d+)?\s?(?:mcg|µg|mg|g|ml|mL|IU)\b/i
        );
        if (strengthMatch) extractedStrength = strengthMatch[0];
      }

      let extractedDosageForm = "";
      if (!drugInfo?.dosageForm && medicationName) {
        const formsRegex = [
          /\btab(?:let)?s?\b/i,
          /\bcap(?:sule)?s?\b/i,
          /\bsuspension\b/i,
          /\bsyrup\b/i,
          /\bcream\b/i,
          /\bointment\b/i,
          /\binjection\b/i,
          /\bdrops\b/i,
          /\bsolution\b/i,
          /\belixir\b/i,
          /\bgel\b/i,
          /\blotion\b/i,
          /\bspray\b/i,
        ];
        const formMatch = formsRegex.find((f) => f.test(medicationName));
        if (formMatch) extractedDosageForm = formMatch.exec(medicationName)?.[0] || "";
      }

      const strengthToShow = drugInfo?.strength || extractedStrength;
      const dosageFormToShow = drugInfo?.dosageForm || extractedDosageForm;
      const packSizeToShow = drugInfo?.packSize || currentForm?.data?.packSize || "";

      if (strengthToShow) medicationDetails += ` ${strengthToShow}`;
      if (dosageFormToShow) medicationDetails += ` ${dosageFormToShow}`;
      if (packSizeToShow) medicationDetails += ` ${packSizeToShow}`;

      const itemCodeValue =
        repeatNumberFromNav != null && String(repeatNumberFromNav).trim() !== ""
          ? String(repeatNumberFromNav).trim()
          : "";

      const repeatText =
        effectiveShowRepeat && repeatData?.displayText
          ? `Repeat ${repeatData.displayText}`
          : "";

      const extractPatientCodes = (patientNameValue) => {
        if (!patientNameValue)
          return { dependentCode: "", mainMemberId: "", cleanName: patientNameValue };

        const codeMatch = patientNameValue.match(/(\d+)\/(\d+)$/);
        if (codeMatch) {
          const dependentCode = codeMatch[1];
          const mainMemberId = codeMatch[2];
          const cleanName = patientNameValue
            .replace(/\s*\d+\/\d+$/, "")
            .trim();
          return { dependentCode, mainMemberId, cleanName };
        }

        return { dependentCode: "", mainMemberId: "", cleanName: patientNameValue };
      };

      const patientCodes = extractPatientCodes(patientName);

      return `
        <div class="printOnlyLabel" style="font-family: Helvetica, Condensed; font-size: 11px; line-height: 1.1; color: #000; padding: 0; margin: 0; font-weight: bold;">
          <div style="margin-bottom: 2px;">
            <span style="font-size: 12px; font-weight: 900;">${
              pharmacyInfo.name || "PHARMACY NAME"
            }</span><br>
            <span style="font-size: 10px; margin-top: -5px; display: block;">${
              pharmacyInfo.address || "ADDRESS"
            }</span><br>
            <span style="font-size:10px; margin-top: -4px; display: block;">${
              pharmacyInfo.phone || "PHONE"
            }</span>
          </div>

          <div style="border-bottom: 1px solid #000000ff; margin-bottom: 2px;"></div>

          <div style="margin-bottom: 3px; font-size: 11px; line-height: 1.4; white-space: nowrap; overflow: hidden;">
            ${medicationDetails}${
        itemCodeValue ? ` (#${itemCodeValue})` : ""
      }${
        drugInfo?.schedule != null && String(drugInfo?.schedule).trim() !== ""
          ? ` S${drugInfo.schedule}`
          : ""
      }
          </div>

          <div style="margin-bottom: 30px; font-size: 12px; line-height: 1.4;">
            ${directions}
          </div>

          <div style="margin-bottom: 1px; font-size: 12px; line-height: 1.4;">
            ${repeatText ? `${repeatText}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp&nbsp;&nbsp;&nbsp;` : ""}Rx : ${rxNumber}
          </div>

          <div style="margin-bottom: 1px; font-size: 12px; line-height: 1.4; white-space: nowrap; overflow: hidden;">
            ${patientCodes.cleanName}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${
        patientCodes.dependentCode
      }/${patientCodes.mainMemberId}
          </div>

          <div style="margin-bottom: 1px; font-size: 12px; line-height: 1.4; white-space: nowrap; overflow: hidden;">
            DOCTOR : ${practitionerName}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${currentDate}
          </div>

          <div style="margin-bottom: 0; font-size: 12px; line-height: 1.4;">
            DISPENSER : ${dispenserName}
          </div>
        </div>
      `;
    };

    const createTracerLabelHTML = async () => {
      const firstForm = allForms[0];
      const rxNumber = String(firstForm?.data?.rxNumber || "").trim() || "1416650";
      const currentDate = new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });

      const cleanPatientName = firstForm?.data?.patientName || "PATIENT NAME";
      const extractPatientCodes = (patientNameValue) => {
        if (!patientNameValue)
          return { dependentCode: "", mainMemberId: "", cleanName: patientNameValue };

        const codeMatch = patientNameValue.match(/(\d+)\/(\d+)$/);
        if (codeMatch) {
          const dependentCode = codeMatch[1];
          const mainMemberId = codeMatch[2];
          const cleanName = patientNameValue
            .replace(/\s*\d+\/\d+$/, "")
            .trim();
          return { dependentCode, mainMemberId, cleanName };
        }

        return { dependentCode: "", mainMemberId: "", cleanName: patientNameValue };
      };

      const patientCodes = extractPatientCodes(cleanPatientName);

      const formatMedicationLine = (form) => {
        const medicationName = form?.data?.medication || "MEDICATION NAME";
        const drugInfo = form?.data?.drugInfo;

        let medicationDetails = medicationName;

        let extractedStrength = "";
        if (!drugInfo?.strength && medicationName) {
          const strengthMatch = medicationName.match(
            /\b\d+(?:\.\d+)?\s?(?:mcg|µg|mg|g|ml|mL|IU)\b/i
          );
          if (strengthMatch) extractedStrength = strengthMatch[0];
        }

        let extractedDosageForm = "";
        if (!drugInfo?.dosageForm && medicationName) {
          const formsRegex = [
            /\btab(?:let)?s?\b/i,
            /\bcap(?:sule)?s?\b/i,
            /\bsuspension\b/i,
            /\bsyrup\b/i,
            /\bcream\b/i,
            /\bointment\b/i,
            /\binjection\b/i,
            /\bdrops\b/i,
            /\bsolution\b/i,
            /\belixir\b/i,
            /\bgel\b/i,
            /\blotion\b/i,
            /\bspray\b/i,
          ];
          const formMatch = formsRegex.find((f) => f.test(medicationName));
          if (formMatch) extractedDosageForm = formMatch.exec(medicationName)?.[0] || "";
        }

        const strengthToShow = drugInfo?.strength || extractedStrength;
        const dosageFormToShow = drugInfo?.dosageForm || extractedDosageForm;
        const packSizeToShow = drugInfo?.packSize || form?.data?.packSize || "";

        if (strengthToShow) medicationDetails += ` ${strengthToShow}`;
        if (dosageFormToShow) medicationDetails += ` ${dosageFormToShow}`;
        if (packSizeToShow) medicationDetails += ` ${packSizeToShow}`;

        return medicationDetails;
      };

      const { searchInventory, getCostPriceBySku } = await import(
        "../../../../api/dispensary/script/LabelApi"
      );

      const costPrices = {};
      let totalCost = 0;
      const medicationLines = [];

      for (const form of allForms) {
        const medicationDetails = formatMedicationLine(form);
        const medicationName = form?.data?.medication;

        try {
          const inventoryResult = await searchInventory(medicationName);

          if (
            inventoryResult.success &&
            inventoryResult.data &&
            inventoryResult.data.length > 0
          ) {
            const sku = inventoryResult.data[0].sku;
            const costPriceResult = await getCostPriceBySku(sku);
            if (costPriceResult.success && costPriceResult.costPrice !== null) {
              costPrices[medicationName] =
                parseFloat(costPriceResult.costPrice) || 0;
              totalCost += costPrices[medicationName];
            } else {
              costPrices[medicationName] = 0;
            }
          } else {
            costPrices[medicationName] = 0;
          }
        } catch (error) {
          console.error(`Error fetching cost price for ${medicationName}:`, error);
          costPrices[medicationName] = 0;
        }

        medicationLines.push({
          medication: medicationDetails,
          costPrice: costPrices[medicationName] || 0,
        });
      }

      const tracerCodeValue =
        repeatNumberFromNav != null && String(repeatNumberFromNav).trim() !== ""
          ? String(repeatNumberFromNav).trim()
          : "";

      return `
        <div class="printOnlyLabel" style="font-family: Helvetica, Condensed; font-size: 11px; line-height: 1.1; color: #000; padding: 0; margin: 0; font-weight: bold;">
          <div style="margin-bottom: 2px;">
            <span style="font-size: 12px; font-weight: 900;">${
              pharmacyInfo.name || "PHARMACY NAME"
            }</span><br>
            <span style="font-size: 10px; margin-top: -5px; display: block;">${
              pharmacyInfo.address || "ADDRESS"
            }</span><br>
            <span style="font-size:10px; margin-top: -4px; display: block;">${
              pharmacyInfo.phone || "PHONE"
            }</span>
          </div>

          <div style="border-bottom: 1px solid #000000ff; margin-bottom: 2px;"></div>

          <div style="margin-bottom: 2px; font-size: 11px; font-weight: bold;">
            Rx : ${rxNumber} &nbsp;&nbsp;&nbsp;&nbsp; ${patientCodes.cleanName} ${
        tracerCodeValue ? `&nbsp;&nbsp;&nbsp;&nbsp; #${tracerCodeValue}` : ""
      }
          </div>

          ${medicationLines
            .map(
              (line, index) => `
                <div style="margin-bottom: 2px; font-size: 10px; white-space: nowrap; position: relative; line-height: 1;">
                  <span>${String(index + 1).padStart(2, "0")} ${
                line.medication
              }</span>
                  <span style="position: absolute; right: 0; top: 0;">${line.costPrice.toFixed(
                    2
                  )}</span>
                </div>
              `
            )
            .join("")}

          <div style="margin-bottom: 0; font-size: 11px; line-height: 1.4; position: relative; white-space: nowrap;">
            <span>${currentDate} ${
        user?.surname?.toUpperCase() || "DISPENSER"
      }${user?.name?.toUpperCase() || "NAME"}+COPY 0.00 =</span>
            <span style="position: absolute; right: 0; top: 0;">${totalCost.toFixed(
              2
            )}</span>
          </div>
        </div>
      `;
    };

    const allLabelElements = [];

    allForms.forEach((form) => {
      const labelHTML = createItemLabelHTML(form);
      const labelDiv = document.createElement("div");
      labelDiv.innerHTML = labelHTML;
      const labelElement = labelDiv.firstElementChild;
      allLabelElements.push(labelElement);
    });

    const tracerLabelHTML = await createTracerLabelHTML();
    const tracerDiv = document.createElement("div");
    tracerDiv.innerHTML = tracerLabelHTML;
    const tracerLabelElement = tracerDiv.firstElementChild;
    allLabelElements.push(tracerLabelElement);

    document.head.appendChild(printStyles);

    allLabelElements.forEach((labelElement) => {
      document.body.appendChild(labelElement);
    });

    window.print();

    setTimeout(() => {
      allLabelElements.forEach((labelElement) => {
        if (document.body.contains(labelElement)) {
          document.body.removeChild(labelElement);
        }
      });
      if (document.head.contains(printStyles)) {
        document.head.removeChild(printStyles);
      }
    }, 1000);
  };

  return (
    <div className={styles.printAllButtonContainer}>
      <Button
        className={styles.printAllButton}
        onClick={handlePrintAllTracer}
        variant="primary"
      >
        Print All
      </Button>
    </div>
  );
}
